<template>
  <div class="pagina-administrar-container">
    <!-- Cabeçalho (Header) Fixo na Cor Premium -->
    <header ref="adminHeaderRef" class="admin-header">
      <div class="titulo-wrapper">
        <h4 class="titulo-principal">{{ titulo }}</h4>
        <span class="titulo-acao">{{ subtitulo }}</span>
      </div>

      <div class="botoes-wrapper">
        <button type="button" class="btn btn-dark" @click="emit('atualizar')" title="Atualizar listagem">
          <i class="bi bi-arrow-clockwise"></i>
        </button>
        <slot
          name="acoes"
          :toggle-filtros="toggleFiltros"
          :filtros-ocultos="filtrosOcultos"
          :filtros-abertos="filtrosAbertos"
        ></slot>
        <button v-if="mostrarBotaoAdicionar" type="button" class="btn btn-primary" @click="emit('adicionar')">
          <i class="bi bi-plus-lg me-2"></i>
          {{ labelBotaoAdicionar }}
        </button>
      </div>
    </header>

    <!-- Conteúdo (Filtros e Listagem) -->
    <div class="admin-conteudo">
      <div v-if="filtrosAbertos" ref="filtrosContainerRef" class="filtros-container shadow-sm">
        <slot name="filtros"></slot>
      </div>

      <slot></slot>
    </div>

    <!-- Rodapé (Paginação Centralizada) -->
    <footer class="admin-footer">
      <slot name="rodape">
        <div v-if="totalItens !== undefined" class="d-flex flex-wrap align-items-center justify-content-between w-100 gap-3">
          <div class="d-flex flex-wrap align-items-center gap-4">
            <span class="text-muted small">
              Página <strong>{{ paginaAtual || 0 }}</strong> de <strong>{{ totalPaginas || 0 }}</strong>
              <span class="d-none d-sm-inline">(Total: {{ totalItens || 0 }} registos)</span>
            </span>

            <div class="d-flex align-items-center gap-2">
              <label class="text-muted small mb-0 d-none d-sm-inline">Itens por página:</label>
              <select
                class="form-select form-select-sm w-auto cursor-pointer"
                :value="itensPorPagina"
                @change="emit('mudar-tamanho-pagina', Number($event.target.value))"
              >
                <option v-for="opcao in opcoesTamanho" :key="opcao" :value="opcao">{{ opcao }}</option>
              </select>
            </div>
          </div>

          <div class="btn-group btn-group-sm">
            <button class="btn btn-outline-secondary btn-sm" @click="emit('mudar-pagina', paginaAtual - 1)" :disabled="paginaAtual <= 1">
              <i class="bi bi-chevron-left"></i>
              <span class="d-none d-sm-inline"> Anterior</span>
            </button>
            <button class="btn btn-outline-secondary btn-sm ms-2" @click="emit('mudar-pagina', paginaAtual + 1)" :disabled="paginaAtual >= totalPaginas">
              <span class="d-none d-sm-inline">Próxima </span>
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>
        </div>
      </slot>
    </footer>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onUnmounted, nextTick } from 'vue'

const props = defineProps({
  titulo: { type: String, required: true, default: 'Administração' },
  subtitulo: { type: String, default: 'Listagem e gerenciamento de registos' },
  labelBotaoAdicionar: { type: String, default: 'Adicionar' },
  filtrosAbertos: { type: Boolean, default: false },
  mostrarBotaoAdicionar: { type: Boolean, default: true },
  totalItens: { type: Number, default: undefined },
  itensPorPagina: { type: Number, default: 25 },
  paginaAtual: { type: Number, default: 1 }
})

const emit = defineEmits(['adicionar', 'atualizar', 'mudar-pagina', 'mudar-tamanho-pagina', 'update:filtrosAbertos'])

const totalPaginas = computed(() => {
  if (!props.totalItens || !props.itensPorPagina) return 0
  return Math.ceil(props.totalItens / props.itensPorPagina)
})

const opcoesTamanho = computed(() => {
  const base = props.itensPorPagina || 25
  return [base, base + 25, base + 50, base + 75]
})

/* ================================================================
 * Filtros — comportamento condicionado ao scroll
 * Detecta quando o painel de filtros fica TOTALMENTE atrás do header
 * (congelado) e expõe `toggleFiltros` ao slot `acoes`, para que o botão
 * de filtros se comporte como se o painel estivesse fechado: ao clicar,
 * rola o container ao topo e reexibe o painel.
 * ================================================================ */
const adminHeaderRef = ref(null)
const filtrosContainerRef = ref(null)
const filtrosOcultos = ref(false)

let _scrollEl = null
let _onScroll = null

const atualizarFiltrosOcultos = () => {
  const header = adminHeaderRef.value
  const panel = filtrosContainerRef.value
  if (!header || !panel) {
    filtrosOcultos.value = false
    return
  }
  // Painel totalmente atrás do header quando sua base está acima da base do header
  filtrosOcultos.value = panel.getBoundingClientRect().bottom <= header.getBoundingClientRect().bottom
}

const rolarContainerAoTopo = () => {
  if (_scrollEl) {
    _scrollEl.scrollTo({ top: 0, behavior: 'smooth' })
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

const toggleFiltros = () => {
  // Se o painel está aberto E visível → fecha.
  // Caso contrário (fechado, ou aberto mas escondido atrás do header) →
  // rola ao topo e garante o painel aberto.
  if (props.filtrosAbertos && !filtrosOcultos.value) {
    emit('update:filtrosAbertos', false)
  } else {
    rolarContainerAoTopo()
    emit('update:filtrosAbertos', true)
  }
}

const _registrarScrollListener = () => {
  let el = adminHeaderRef.value ? adminHeaderRef.value.parentElement : null
  while (el) {
    const cs = getComputedStyle(el)
    if (/(auto|scroll)/.test(cs.overflowY)) break
    el = el.parentElement
  }
  _scrollEl = el
  _onScroll = () => atualizarFiltrosOcultos()
  if (_scrollEl) _scrollEl.addEventListener('scroll', _onScroll, { passive: true })
  window.addEventListener('resize', _onScroll, { passive: true })
}

onMounted(() => {
  _registrarScrollListener()
  atualizarFiltrosOcultos()
})

onUnmounted(() => {
  if (_scrollEl && _onScroll) _scrollEl.removeEventListener('scroll', _onScroll)
  if (_onScroll) window.removeEventListener('resize', _onScroll)
})

// Reavalia quando o painel é aberto/fechado (muda o layout)
watch(() => props.filtrosAbertos, () => {
  nextTick(atualizarFiltrosOcultos)
})
</script>

<style scoped>
.pagina-administrar-container {
  /*
   * GUARDIÃO: altura do header sticky do PaginaAdministrar, exposta como
   * variável CSS para que os grids (AdminTable/ExcelGrid) possam congelar o
   * próprio thead (e a toolbar) logo abaixo dele, sem hard-code.
   * No mobile o header deixa de ser sticky (position: static) → vira 0.
   */
  --ct-admin-header-h: 100px;
  width: 100%;
  position: relative;
  padding-bottom: 1rem;
}

/* O Cabeçalho continua com a cor fixa da marca */
.admin-header {
  display: flex;
  height: 100px;
  align-items: center;
  gap: 60px;
  padding: 1.5rem;
  background-color: #34495E;
  border-bottom: 1px solid var(--bs-border-color); /* Linha divisória adaptável */
  position: sticky;
  top: 0;
  z-index: 10;
}

.titulo-wrapper {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.titulo-principal {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 1.1;
  color: #FFFFFF;
}

.titulo-acao {
  font-size: 0.875rem;
  color: #ADB5BD;
}

.botoes-wrapper {
  margin-left: auto;
  flex-shrink: 0;
  display: flex;
  gap: 0.75rem;
}

/* Corpo e áreas dinâmicas usando variáveis Bootstrap */
.admin-conteudo {
  padding: 1.5rem;
  background-color: var(--bs-body-bg);
  padding-bottom: 3rem;
  transition: background-color 0.3s ease;
}

.filtros-container {
  padding: 1.5rem;
  background-color: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 0.25rem;
  margin-bottom: 1.5rem;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.admin-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--bs-border-color);
  background-color: var(--bs-tertiary-bg);
  position: sticky;
  bottom: -1.5rem;
  z-index: 20;
  box-shadow: 0 -5px 20px rgba(0,0,0,0.15);
  border-radius: 0.25rem;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

/* --- ESTILOS DE GRELHA INJETADOS (VIA :DEEP) --- */
:deep(.admin-table-container) {
  background-color: transparent;
  /* GUARDIÃO: o overflow-x é controlado pelo próprio AdminTable
     (base sem overflow + classe .table-overflows-x quando a tabela excede). */
}

:deep(.admin-table) {
  width: 100%;
  /*
   * GUARDIÃO: `separate` (em vez de `collapse`) é OBRIGATÓRIO para o
   * `thead th` sticky — com `collapse` o Chrome desloca a linha do cabeçalho
   * ao congelar e faz as bordas verticais sumirem. `border-spacing: 0` mantém
   * o visual de grade sem espaçamento entre células.
   */
  border-collapse: separate;
  border-spacing: 0;
}

:deep(.admin-table thead th) {
  background-color: var(--bs-tertiary-bg);
  font-weight: 600;
  color: var(--bs-heading-color);
  border-bottom: 1px solid var(--bs-border-color);
  transition: background-color 0.3s ease;
}

:deep(.admin-table th) {
  padding: 0.75rem 1.25rem;
  height: 45px;
  text-align: left;
  border-bottom: 1px solid var(--bs-border-color);
  vertical-align: middle;
  color: var(--bs-body-color);
  transition: background-color 0.2s ease-out, color 0.3s ease, border-color 0.3s ease;
}

:deep(.admin-table td) {
  padding: 0.75rem 1.25rem;
  height: 45px;
  text-align: left;
  border-bottom: 1px solid var(--bs-border-color);
  vertical-align: middle;
  white-space: nowrap;
  color: var(--bs-body-color);
  transition: background-color 0.2s ease-out, color 0.3s ease, border-color 0.3s ease;
}

:deep(.admin-table tbody tr:hover td) {
  background-color: var(--bs-secondary-bg);
}

:deep(.admin-table tbody tr:last-child td) {
  border-bottom: none;
}

:deep(.acoes-cell) {
  text-align: right;
  position: relative;
}

:deep(.acoes-wrapper) {
  display: flex;
  justify-content: flex-end;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.2s ease-out;
}

:deep(tr:hover .acoes-wrapper) {
  opacity: 1;
}

@media (max-width: 991.98px) {
  /* Header deixa de ser sticky no mobile → offset de congelamento dos grids vira 0 */
  .pagina-administrar-container { --ct-admin-header-h: 0px; }

  .admin-header {
    position: static;
    height: auto;
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  .titulo-wrapper { align-items: center; text-align: center; }
  .botoes-wrapper { margin-left: 0; display: flex; flex-direction: column; width: 100%; }
  .botoes-wrapper .btn { width: 100%; }
  .admin-conteudo { padding: 1rem; }

  /* Paginação posicionada ao final do grid, não mais sticky */
  .admin-footer {
    position: static;
    box-shadow: none;
    border-radius: 0;
    margin-top: 1rem;
  }
}
</style>
